/**
 * 用户管理主页面
 *
 * @description 管理员和交易所的用户管理功能
 * @version 1.0.0
 * @author AI Assistant
 * @date 2025-09-24
 *
 * @features
 * - 用户列表查询和展示
 * - 用户创建、编辑、删除
 * - 密码重置功能
 * - 用户状态管理
 * - 权限控制
 */

import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Space,
  Input,
  Select,
  DatePicker,
  Modal,
  Form,
  Tooltip,
  Card,
  Row,
  Col,
  Statistic,
  Badge,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  ExportOutlined,
  EditOutlined,
  DeleteOutlined,
  KeyOutlined,
  MailOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  UserOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';

import StandardPageLayout from '../../components/layout/StandardPageLayout';
import {
  UnifiedDataTable,
  type UnifiedTableColumn,
} from '../../components/unified/UnifiedDataTable';
import {
  UserStatusTag,
  UserRoleTag,
} from '../../components/unified/UnifiedStatusTag';
import { useUnifiedConfirm, UnifiedFilters } from '../../components/unified';
import { App } from 'antd';
import { useAuth } from '../../hooks/useAuth';

import {
  useGetUsersQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useResetUserPasswordMutation,
  useResendActivationMutation,
  useUpdateUserStatusMutation,
  useDeleteUserMutation,
  useGetUserStatisticsQuery,
} from '../../store/api/userManagementApi';

import type {
  User,
  UserTableRow,
  UserQueryParams,
  CreateUserRequest,
  UpdateUserRequest,
  UserRole,
  UserStatus,
  UserFormData,
  PasswordResetFormData,
} from '../../types/user-management';

const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

// 统一的输入框主题样式
const inputThemeStyle = {
  borderColor: '#10b981',
};

const selectThemeStyle = {
  borderColor: '#10b981',
};

// 角色选项值配置（翻译在组件内通过 t() 获取）
const ROLE_VALUES = [
  { value: 'admin' as UserRole, color: '#722ed1' },
  { value: 'exchange' as UserRole, color: '#1890ff' },
  { value: 'issuer' as UserRole, color: '#13c2c2' },
  { value: 'service_provider' as UserRole, color: '#52c41a' },
];

// 状态选项值配置（翻译在组件内通过 t() 获取）
const STATUS_VALUES = [
  { value: 'active' as UserStatus, color: '#52c41a' },
  { value: 'pending' as UserStatus, color: '#fa8c16' },
  { value: 'inactive' as UserStatus, color: '#8c8c8c' },
  { value: 'suspended' as UserStatus, color: '#faad14' },
  { value: 'deleted' as UserStatus, color: '#ff4d4f' },
];

const UserManagement: React.FC = () => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const confirm = useUnifiedConfirm();
  const { message } = App.useApp();

  // 页面配置（使用翻译）
  const pageTitle = t('user:title');
  const pageSubtitle = t('user:subtitle');
  const breadcrumbs = [
    { label: t('user:breadcrumb.home'), path: '/admin' },
    { label: t('user:breadcrumb.system') },
    { label: t('user:breadcrumb.userManagement') },
  ];

  // 角色选项配置（使用翻译）
  const ROLE_OPTIONS = ROLE_VALUES.map(role => ({
    ...role,
    label: t(`user:role.${role.value}`),
  }));

  // 状态选项配置（使用翻译）
  const STATUS_OPTIONS = STATUS_VALUES.map(status => ({
    ...status,
    label: t(`user:status.${status.value}`),
  }));

  // 状态管理 - 默认无筛选条件，按创建时间降序排列
  const [queryParams, setQueryParams] = useState<UserQueryParams>({
    page: 1,
    limit: 20,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [passwordResetModalVisible, setPasswordResetModalVisible] =
    useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // 表单实例
  const [createForm] = Form.useForm<UserFormData>();
  const [editForm] = Form.useForm<UserFormData>();
  const [passwordResetForm] = Form.useForm<PasswordResetFormData>();

  // API Hooks
  const {
    data: usersResponse,
    isLoading: usersLoading,
    refetch: refetchUsers,
  } = useGetUsersQuery(queryParams);

  const { data: statisticsData } = useGetUserStatisticsQuery();

  const [createUser, { isLoading: createLoading }] = useCreateUserMutation();
  const [updateUser, { isLoading: updateLoading }] = useUpdateUserMutation();
  const [resetPassword, { isLoading: resetLoading }] =
    useResetUserPasswordMutation();
  const [resendActivation, { isLoading: resendActivationLoading }] =
    useResendActivationMutation();
  const [updateStatus] = useUpdateUserStatusMutation();
  const [deleteUser] = useDeleteUserMutation();

  // 权限检查
  const canCreateUser = useMemo(() => {
    return currentUser?.role === 'admin' || currentUser?.role === 'exchange';
  }, [currentUser?.role]);

  const canEditUser = useCallback(
    (user: User) => {
      if (currentUser?.role === 'admin') return true;
      if (currentUser?.role === 'exchange') {
        return ['issuer', 'service_provider'].includes(user.role);
      }
      return false;
    },
    [currentUser?.role]
  );

  const canDeleteUser = useCallback(
    (user: User) => {
      if (currentUser?.role === 'admin') return !user.isBuiltIn;
      return false;
    },
    [currentUser?.role]
  );

  // 获取允许创建的角色
  const getAllowedRoles = useMemo(() => {
    if (currentUser?.role === 'admin') {
      return ROLE_OPTIONS;
    } else if (currentUser?.role === 'exchange') {
      return ROLE_OPTIONS.filter(role =>
        ['issuer', 'service_provider'].includes(role.value)
      );
    }
    return [];
  }, [currentUser?.role]);

  // 处理搜索
  const handleSearch = useCallback((value: string) => {
    setQueryParams(prev => ({
      ...prev,
      search: value.trim() || undefined,
      page: 1,
    }));
  }, []);

  // 处理筛选
  const handleFilterChange = useCallback(
    (key: keyof UserQueryParams, value: any) => {
      setQueryParams(prev => ({
        ...prev,
        [key]: value,
        page: 1,
      }));
    },
    []
  );

  // 处理表格变化
  const handleTableChange = useCallback(
    (pagination: any, filters: any, sorter: any) => {
      setQueryParams(prev => ({
        ...prev,
        page: pagination.current,
        limit: pagination.pageSize,
        sortBy: sorter.field || 'createdAt',
        sortOrder: sorter.order === 'ascend' ? 'asc' : 'desc',
        // 处理表格列筛选器
        role: filters.role || undefined,
        status: filters.status || undefined,
      }));
    },
    []
  );

  // 处理创建用户
  const handleCreateUser = useCallback(
    async (values: UserFormData) => {
      try {
        const createData: CreateUserRequest = {
          email: values.email,
          name: values.name,
          role: values.role,
          companyName: values.companyName || undefined,
          position: values.position || undefined,
          permissions: values.permissions || undefined,
        };

        const result = await createUser(createData).unwrap();

        if (result.success) {
          message.success(t('user:message.createSuccess'));

          // 如果有自动生成的密码，显示给管理员
          if (result.data?.generatedPassword) {
            Modal.info({
              title: t('user:modal.createSuccessTitle'),
              content: (
                <div>
                  <p>{t('user:modal.createSuccessContent')}</p>
                  <p>
                    <strong>{t('user:modal.loginEmail')}</strong>
                    {values.email}
                  </p>
                  <p>
                    <strong>{t('user:modal.initialPassword')}</strong>
                    <code style={{ background: '#f5f5f5', padding: '2px 4px' }}>
                      {result.data.generatedPassword}
                    </code>
                  </p>
                  <p style={{ color: '#faad14', fontSize: '12px' }}>
                    ⚠️ {t('user:modal.passwordSafetyWarning')}
                  </p>
                </div>
              ),
              width: 500,
            });
          }

          setCreateModalVisible(false);
          createForm.resetFields();
        }
      } catch (error: any) {
        message.error(
          error?.data?.error?.message || t('user:message.createFailed')
        );
      }
    },
    [createUser, createForm, message]
  );

  // 处理编辑用户
  const handleEditUser = useCallback(
    async (values: UserFormData) => {
      if (!selectedUser) return;

      try {
        const updateData: UpdateUserRequest = {
          id: selectedUser._id,
          email: values.email,
          name: values.name,
          role: values.role,
          companyName: values.companyName || undefined,
          position: values.position || undefined,
          permissions: values.permissions || undefined,
        };

        const result = await updateUser(updateData).unwrap();

        if (result.success) {
          message.success(t('user:message.updateSuccess'));
          setEditModalVisible(false);
          setSelectedUser(null);
          editForm.resetFields();
        }
      } catch (error: any) {
        message.error(
          error?.data?.error?.message || t('user:message.updateFailed')
        );
      }
    },
    [updateUser, selectedUser, editForm, message]
  );

  // 处理密码重置
  const handleResendActivation = useCallback(
    async (user: User) => {
      try {
        const result = await resendActivation({ id: user._id }).unwrap();
        if (result.success) {
          message.success(
            t('user:message.resendActivationSuccess', {
              email: result.data?.email || user.email,
            })
          );
        } else {
          message.error(
            result.error?.message || t('user:message.resendActivationFailed')
          );
        }
      } catch (error: any) {
        message.error(
          error?.data?.error?.message ||
            t('user:message.resendActivationFailed')
        );
      }
    },
    [resendActivation, message, t]
  );

  const handleResetPassword = useCallback(
    async (values: PasswordResetFormData) => {
      if (!selectedUser) return;

      try {
        const result = await resetPassword({
          id: selectedUser._id,
          generateNew: values.generateNew,
          newPassword: values.generateNew ? undefined : values.newPassword,
          forceChange: values.forceChange,
        }).unwrap();

        if (result.success) {
          message.success(t('user:message.resetPasswordSuccess'));

          // 如果有新生成的密码，显示给管理员
          if (result.data?.newPassword) {
            Modal.info({
              title: t('user:modal.resetSuccessTitle'),
              content: (
                <div>
                  <p>
                    <strong>{t('user:modal.userLabel')}</strong>
                    {selectedUser.name} ({selectedUser.email})
                  </p>
                  <p>
                    <strong>{t('user:modal.newPasswordLabel')}</strong>
                    <code style={{ background: '#f5f5f5', padding: '2px 4px' }}>
                      {result.data.newPassword}
                    </code>
                  </p>
                  <p style={{ color: '#faad14', fontSize: '12px' }}>
                    ⚠️ {t('user:modal.newPasswordWarning')}
                  </p>
                </div>
              ),
              width: 500,
            });
          }

          setPasswordResetModalVisible(false);
          setSelectedUser(null);
          passwordResetForm.resetFields();
        }
      } catch (error: any) {
        message.error(
          error?.data?.error?.message || t('user:message.resetPasswordFailed')
        );
      }
    },
    [resetPassword, selectedUser, passwordResetForm, message]
  );

  // 处理状态变更
  const handleStatusChange = useCallback(
    async (user: User, newStatus: UserStatus, reason?: string) => {
      try {
        const result = await updateStatus({
          id: user._id,
          status: newStatus,
          reason,
        }).unwrap();

        if (result.success) {
          message.success(
            t('user:message.statusUpdated', {
              status: STATUS_OPTIONS.find(s => s.value === newStatus)?.label,
            })
          );
        }
      } catch (error: any) {
        message.error(
          error?.data?.error?.message || t('user:message.statusUpdateFailed')
        );
      }
    },
    [updateStatus, message]
  );

  // 处理删除用户
  const handleDeleteUser = useCallback(
    async (user: User) => {
      try {
        const result = await deleteUser(user._id).unwrap();

        if (result.success) {
          message.success(t('user:message.deleteSuccess'));
        }
      } catch (error: any) {
        message.error(
          error?.data?.error?.message || t('user:message.deleteFailed')
        );
      }
    },
    [deleteUser, message]
  );

  // 表格列配置
  const columns: UnifiedTableColumn<UserTableRow>[] = useMemo(
    () => [
      {
        title: t('user:field.name'),
        key: 'userInfo',
        width: 200,
        fixed: 'left',
        render: (_, record) => (
          <div>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>
              {record.name}
            </div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              {record.email}
            </div>
            {record.companyName && (
              <div style={{ fontSize: '12px', color: '#999' }}>
                {record.companyName}
              </div>
            )}
          </div>
        ),
      },
      {
        title: t('user:field.role'),
        dataIndex: 'role',
        key: 'role',
        width: 100,
        filters: ROLE_OPTIONS.map(role => ({
          text: role.label,
          value: role.value,
        })),
        filteredValue: queryParams.role || null,
        render: (role: UserRole) => <UserRoleTag status={role} />,
      },
      {
        title: t('user:field.status'),
        dataIndex: 'status',
        key: 'status',
        width: 100,
        filters: STATUS_OPTIONS.map(status => ({
          text: status.label,
          value: status.value,
        })),
        filteredValue: queryParams.status || null,
        render: (status: UserStatus) => <UserStatusTag status={status} />,
      },
      {
        title: t('user:field.position'),
        dataIndex: 'position',
        key: 'position',
        width: 120,
        render: (position: string) => position || '-',
      },
      {
        title: t('user:field.lastLogin'),
        dataIndex: 'lastLoginAt',
        key: 'lastLoginAt',
        width: 140,
        sorter: true,
        render: (lastLoginAt: string) =>
          lastLoginAt
            ? dayjs(lastLoginAt).format('YYYY-MM-DD HH:mm')
            : t('user:field.neverLoggedIn'),
      },
      {
        title: t('user:field.createTime'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 140,
        sorter: true,
        render: (createdAt: string) =>
          dayjs(createdAt).format('YYYY-MM-DD HH:mm'),
      },
      {
        title: t('user:field.operation'),
        key: 'actions',
        width: 200,
        fixed: 'right',
        render: (_, record) => {
          const user = record as User;
          const actions = [];

          // 编辑按钮
          if (canEditUser(user)) {
            actions.push(
              <Tooltip title={t('user:action.edit')} key="edit">
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => {
                    setSelectedUser(user);
                    editForm.setFieldsValue({
                      email: user.email,
                      name: user.name,
                      role: user.role,
                      companyName: user.companyName || '',
                      position: user.position || '',
                      permissions: user.permissions || [],
                    });
                    setEditModalVisible(true);
                  }}
                  style={{
                    color: '#1890ff',
                    borderColor: '#1890ff',
                  }}
                />
              </Tooltip>
            );

            // 重置密码按钮
            actions.push(
              <Tooltip title={t('user:action.resetPassword')} key="password">
                <Button
                  type="text"
                  size="small"
                  icon={<KeyOutlined />}
                  onClick={() => {
                    setSelectedUser(user);
                    passwordResetForm.setFieldsValue({
                      generateNew: true,
                      forceChange: false,
                    });
                    setPasswordResetModalVisible(true);
                  }}
                  style={{
                    color: '#faad14',
                    borderColor: '#faad14',
                  }}
                />
              </Tooltip>
            );

            // 补发激活邮件按钮（仅待激活账户）
            if (user.status === 'pending') {
              actions.push(
                <Tooltip
                  title={t('user:action.resendActivation')}
                  key="resendActivation"
                >
                  <Button
                    type="text"
                    size="small"
                    icon={<MailOutlined />}
                    loading={resendActivationLoading}
                    onClick={() => {
                      confirm.show({
                        title: t('user:action.resendActivation'),
                        content: (
                          <div>
                            <p>
                              {t('user:message.resendActivationConfirm', {
                                name: user.name,
                              })}
                            </p>
                            <p style={{ color: '#fa8c16', fontSize: 12 }}>
                              {t('user:message.resendActivationWarning', {
                                email: user.email,
                              })}
                            </p>
                          </div>
                        ),
                        onConfirm: () => handleResendActivation(user),
                        okText: t('user:action.resendActivation'),
                        type: 'warning',
                      });
                    }}
                    style={{
                      color: '#fa8c16',
                      borderColor: '#fa8c16',
                    }}
                  />
                </Tooltip>
              );
            }

            // 状态切换按钮
            if (user.status === 'active') {
              actions.push(
                <Tooltip title={t('user:action.suspendUser')} key="suspend">
                  <Button
                    type="text"
                    size="small"
                    icon={<PauseCircleOutlined />}
                    onClick={() => {
                      confirm.show({
                        title: t('user:modal.suspendTitle'),
                        content: t('user:message.suspendConfirm', {
                          name: user.name,
                        }),
                        onConfirm: () =>
                          handleStatusChange(
                            user,
                            'suspended',
                            t('user:message.adminSuspend')
                          ),
                        okText: t('user:modal.confirmSuspend'),
                        type: 'warning',
                      });
                    }}
                    style={{
                      color: '#faad14',
                      borderColor: '#faad14',
                    }}
                  />
                </Tooltip>
              );
            } else if (user.status === 'suspended') {
              actions.push(
                <Tooltip title={t('user:action.activateUser')} key="activate">
                  <Button
                    type="text"
                    size="small"
                    icon={<PlayCircleOutlined />}
                    onClick={() =>
                      handleStatusChange(
                        user,
                        'active',
                        t('user:message.adminActivate')
                      )
                    }
                    style={{
                      color: '#52c41a',
                      borderColor: '#52c41a',
                    }}
                  />
                </Tooltip>
              );
            }
          }

          // 删除按钮（仅管理员，且用户未被删除）
          if (user.status !== 'deleted' && canDeleteUser(user)) {
            actions.push(
              <Tooltip title={t('user:action.delete')} key="delete">
                <Button
                  type="text"
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    confirm.show({
                      title: t('user:modal.deleteTitle'),
                      content: (
                        <div>
                          <p>
                            {t('user:message.deleteConfirm', {
                              name: user.name,
                            })}
                          </p>
                          <p style={{ color: '#ff4d4f', fontSize: '12px' }}>
                            ⚠️ {t('user:message.deleteDataWarning')}
                          </p>
                        </div>
                      ),
                      onConfirm: () => handleDeleteUser(user),
                      okText: t('user:modal.confirmDelete'),
                      type: 'error',
                    });
                  }}
                  style={{
                    color: '#ff4d4f',
                    borderColor: '#ff4d4f',
                  }}
                />
              </Tooltip>
            );
          }

          return <Space size="small">{actions}</Space>;
        },
      },
    ],
    [
      queryParams.role,
      queryParams.status,
      canEditUser,
      canDeleteUser,
      editForm,
      passwordResetForm,
      handleStatusChange,
      handleDeleteUser,
      handleResendActivation,
      resendActivationLoading,
      confirm,
    ]
  );

  // 操作按钮配置 - 暂时隐藏新增用户和刷新按钮
  const actions = useMemo(
    () => [
      // 暂时隐藏新增用户按钮
      // ...(canCreateUser
      //   ? [
      //       {
      //         key: 'create',
      //         label: '新增用户',
      //         type: 'primary' as const,
      //         icon: <PlusOutlined />,
      //         onClick: () => {
      //           createForm.resetFields();
      //           setCreateModalVisible(true);
      //         },
      //       },
      //     ]
      //   : []),
      // 暂时隐藏刷新按钮
      // {
      //   key: 'refresh',
      //   label: '刷新',
      //   icon: <ReloadOutlined />,
      //   onClick: () => refetchUsers(),
      // },
      // Bug #20: 暂时隐藏未实现的导出功能
      // {
      //   key: 'export',
      //   label: '导出',
      //   icon: <ExportOutlined />,
      //   onClick: () => message.info('导出功能待实现'),
      // },
    ],
    [canCreateUser, createForm, refetchUsers, message]
  );

  // 转换数据格式
  const tableData: UserTableRow[] = useMemo(() => {
    return (
      usersResponse?.data?.users?.map((user: User) => ({
        ...user,
        key: user._id,
      })) || []
    );
  }, [usersResponse?.data?.users]);

  return (
    <StandardPageLayout
      title={pageTitle}
      subtitle={t('user:subtitle')}
      breadcrumbs={breadcrumbs}
      statistics={[
        {
          value: usersResponse?.data?.total ?? statisticsData?.total ?? 0,
          label: t('user:stats.total'),
          color: '#262626',
        },
        {
          value: statisticsData?.byStatus?.active || 0,
          label: t('user:stats.active'),
          color: '#10b981',
        },
        {
          value: statisticsData?.activeToday || 0,
          label: t('user:stats.activeToday'),
          color: '#52c41a',
        },
        {
          value: statisticsData?.newUsersThisMonth || 0,
          label: t('user:stats.newThisMonth'),
          color: '#1890ff',
        },
      ]}
      actions={actions}
      icon={<UserOutlined style={{ fontSize: '24px', color: 'white' }} />}
      tags={[
        t('user:tags.userManagement'),
        t('user:tags.permissionControl'),
        t('user:tags.dataStatistics'),
      ]}
    >
      {/* 搜索筛选区域 */}
      <UnifiedFilters
        filters={queryParams}
        filterConfigs={[
          {
            key: 'search',
            type: 'search',
            placeholder: t('user:filter.searchPlaceholder'),
            span: 8,
          },
          {
            key: 'role',
            type: 'multiSelect',
            placeholder: t('user:filter.rolePlaceholder'),
            span: 4,
            options: ROLE_OPTIONS.map(role => ({
              label: role.label,
              value: role.value,
            })),
          },
          {
            key: 'status',
            type: 'multiSelect',
            placeholder: t('user:filter.statusPlaceholder'),
            span: 4,
            options: STATUS_OPTIONS.map(status => ({
              label: status.label,
              value: status.value,
            })),
          },
          {
            key: 'dateRange',
            type: 'dateRange',
            activeKeys: ['lastLoginFrom', 'lastLoginTo'],
            placeholder: [
              t('user:filter.lastLoginStart'),
              t('user:filter.lastLoginEnd'),
            ],
            span: 6,
          },
          {
            key: 'isBuiltIn',
            type: 'select',
            placeholder: t('user:filter.builtInPlaceholder'),
            span: 2,
            options: [
              { label: t('common:label.yes'), value: true },
              { label: t('common:label.no'), value: false },
            ],
          },
        ]}
        onFiltersChange={(key, value) => {
          if (key === 'search') {
            handleSearch(value);
          } else if (key === 'dateRange') {
            // Bugfix: 同步维护 dateRange，确保 UnifiedFilters 能正确识别活跃筛选条件
            if (value && value[0] && value[1]) {
              setQueryParams(prev => ({
                ...prev,
                page: 1,
                dateRange: value,
                lastLoginFrom: value[0].toISOString(),
                lastLoginTo: value[1].toISOString(),
              }));
            } else {
              setQueryParams(prev => ({
                ...prev,
                page: 1,
                dateRange: undefined,
                lastLoginFrom: undefined,
                lastLoginTo: undefined,
              }));
            }
          } else {
            handleFilterChange(key as keyof UserQueryParams, value);
          }
        }}
        onClearFilters={() => {
          // Bug修复: 清除所有筛选条件，不设置任何默认值
          setQueryParams(prev => ({
            page: 1,
            limit: prev.limit, // 保持当前分页大小
            sortBy: prev.sortBy,
            sortOrder: prev.sortOrder,
            search: undefined,
            role: undefined,
            status: undefined, // 清除状态筛选
            lastLoginFrom: undefined,
            lastLoginTo: undefined,
            dateRange: undefined,
            isBuiltIn: undefined,
          }));
        }}
      />

      {/* 用户数据表格 */}
      <UnifiedDataTable
        columns={columns}
        dataSource={tableData}
        loading={usersLoading}
        pagination={{
          current: queryParams.page,
          pageSize: queryParams.limit,
          total: usersResponse?.data?.total || 0,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            t('common:pagination.range', {
              start: range[0],
              end: range[1],
              total,
            }),
        }}
        rowSelection={{
          selectedRowKeys,
          onChange: (selectedRowKeys: React.Key[]) => {
            setSelectedRowKeys(selectedRowKeys as string[]);
          },
          getCheckboxProps: record => ({
            disabled: record.isBuiltIn, // 内置用户不能批量操作
          }),
        }}
        onChange={handleTableChange}
        scroll={{ x: 1200 }}
      />

      {/* 创建用户弹窗 */}
      <Modal
        title={
          <div
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              padding: '16px 24px',
              margin: '-16px -24px 16px -24px',
              borderRadius: '8px 8px 0 0',
            }}
          >
            <Space>
              <UserOutlined style={{ fontSize: '18px' }} />
              <span style={{ fontSize: '16px', fontWeight: 600 }}>
                {t('user:action.create')}
              </span>
            </Space>
          </div>
        }
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          createForm.resetFields();
        }}
        footer={
          <div
            style={{
              background:
                'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(5, 150, 105, 0.02) 100%)',
              padding: '16px 24px',
              margin: '16px -24px -16px -24px',
              borderTop: '1px solid rgba(16, 185, 129, 0.1)',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '8px',
              }}
            >
              <Button
                onClick={() => {
                  setCreateModalVisible(false);
                  createForm.resetFields();
                }}
              >
                {t('common:action.cancel')}
              </Button>
              <Button
                type="primary"
                loading={createLoading}
                onClick={() => createForm.submit()}
                style={{
                  background:
                    'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  borderColor: '#10b981',
                }}
              >
                {t('user:action.create')}
              </Button>
            </div>
          </div>
        }
        width={600}
        destroyOnHidden
        styles={{
          body: {
            padding: '24px',
            background:
              'linear-gradient(135deg, rgba(16, 185, 129, 0.01) 0%, rgba(255, 255, 255, 0.95) 100%)',
          },
        }}
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreateUser}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="email"
                label={t('user:field.email')}
                rules={[
                  {
                    required: true,
                    message: t('user:validation.emailRequired'),
                  },
                  { type: 'email', message: t('user:validation.emailFormat') },
                ]}
              >
                <Input
                  placeholder={t('user:field.userEmailPlaceholder')}
                  style={inputThemeStyle}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="name"
                label={t('user:field.name')}
                rules={[
                  {
                    required: true,
                    message: t('user:validation.nameRequired'),
                  },
                ]}
              >
                <Input
                  placeholder={t('user:field.userNamePlaceholder')}
                  style={inputThemeStyle}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="role"
                label={t('user:field.role')}
                rules={[
                  {
                    required: true,
                    message: t('user:validation.roleRequired'),
                  },
                ]}
              >
                <Select
                  placeholder={t('user:field.userRolePlaceholder')}
                  style={selectThemeStyle}
                >
                  {getAllowedRoles.map(role => (
                    <Option key={role.value} value={role.value}>
                      <Badge color={role.color} text={role.label} />
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="position" label={t('user:field.position')}>
                <Input
                  placeholder={t('user:field.positionPlaceholder')}
                  style={inputThemeStyle}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="companyName" label={t('user:field.companyName')}>
            <Input
              placeholder={t('user:field.companyNamePlaceholder')}
              style={inputThemeStyle}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 编辑用户弹窗 */}
      <Modal
        title={
          <div
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              padding: '16px 24px',
              margin: '-16px -24px 16px -24px',
              borderRadius: '8px 8px 0 0',
            }}
          >
            <Space>
              <EditOutlined style={{ fontSize: '18px' }} />
              <span style={{ fontSize: '16px', fontWeight: 600 }}>
                {t('user:action.edit')}
              </span>
            </Space>
          </div>
        }
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setSelectedUser(null);
          editForm.resetFields();
        }}
        footer={
          <div
            style={{
              background:
                'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(5, 150, 105, 0.02) 100%)',
              padding: '16px 24px',
              margin: '16px -24px -16px -24px',
              borderTop: '1px solid rgba(16, 185, 129, 0.1)',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '8px',
              }}
            >
              <Button
                onClick={() => {
                  setEditModalVisible(false);
                  setSelectedUser(null);
                  editForm.resetFields();
                }}
              >
                {t('common:action.cancel')}
              </Button>
              <Button
                type="primary"
                loading={updateLoading}
                onClick={() => editForm.submit()}
                style={{
                  background:
                    'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  borderColor: '#10b981',
                }}
              >
                {t('user:action.saveChanges')}
              </Button>
            </div>
          </div>
        }
        width={600}
        destroyOnHidden
        styles={{
          body: {
            padding: '24px',
            background:
              'linear-gradient(135deg, rgba(16, 185, 129, 0.01) 0%, rgba(255, 255, 255, 0.95) 100%)',
          },
        }}
      >
        <Form form={editForm} layout="vertical" onFinish={handleEditUser}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="email"
                label={t('user:field.email')}
                rules={[
                  {
                    required: true,
                    message: t('user:validation.emailRequired'),
                  },
                  { type: 'email', message: t('user:validation.emailFormat') },
                ]}
              >
                <Input
                  placeholder={t('user:field.userEmailPlaceholder')}
                  style={inputThemeStyle}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="name"
                label={t('user:field.name')}
                rules={[
                  {
                    required: true,
                    message: t('user:validation.nameRequired'),
                  },
                ]}
              >
                <Input
                  placeholder={t('user:field.userNamePlaceholder')}
                  style={inputThemeStyle}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="role"
                label={t('user:field.role')}
                rules={[
                  {
                    required: true,
                    message: t('user:validation.roleRequired'),
                  },
                ]}
              >
                <Select
                  placeholder={t('user:field.userRolePlaceholder')}
                  disabled={currentUser?.role !== 'admin'}
                >
                  {getAllowedRoles.map(role => (
                    <Option key={role.value} value={role.value}>
                      <Badge color={role.color} text={role.label} />
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="position" label={t('user:field.position')}>
                <Input
                  placeholder={t('user:field.positionPlaceholder')}
                  style={inputThemeStyle}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="companyName" label={t('user:field.companyName')}>
            <Input
              placeholder={t('user:field.companyNamePlaceholder')}
              style={inputThemeStyle}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 密码重置弹窗 */}
      <Modal
        title={
          <div
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              padding: '16px 24px',
              margin: '-16px -24px 16px -24px',
              borderRadius: '8px 8px 0 0',
            }}
          >
            <Space>
              <KeyOutlined style={{ fontSize: '18px' }} />
              <span style={{ fontSize: '16px', fontWeight: 600 }}>
                {t('user:action.resetPassword')} - {selectedUser?.name}
              </span>
            </Space>
          </div>
        }
        open={passwordResetModalVisible}
        onCancel={() => {
          setPasswordResetModalVisible(false);
          setSelectedUser(null);
          passwordResetForm.resetFields();
        }}
        footer={
          <div
            style={{
              background:
                'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(5, 150, 105, 0.02) 100%)',
              padding: '16px 24px',
              margin: '16px -24px -16px -24px',
              borderTop: '1px solid rgba(16, 185, 129, 0.1)',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '8px',
              }}
            >
              <Button
                onClick={() => {
                  setPasswordResetModalVisible(false);
                  setSelectedUser(null);
                  passwordResetForm.resetFields();
                }}
              >
                {t('common:action.cancel')}
              </Button>
              <Button
                type="primary"
                loading={resetLoading}
                onClick={() => passwordResetForm.submit()}
                style={{
                  background:
                    'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  borderColor: '#10b981',
                }}
              >
                {t('user:action.resetPassword')}
              </Button>
            </div>
          </div>
        }
        width={500}
        destroyOnHidden
        styles={{
          body: {
            padding: '24px',
            background:
              'linear-gradient(135deg, rgba(16, 185, 129, 0.01) 0%, rgba(255, 255, 255, 0.95) 100%)',
          },
        }}
      >
        <Form
          form={passwordResetForm}
          layout="vertical"
          onFinish={handleResetPassword}
        >
          <Form.Item
            name="generateNew"
            label={t('user:password.settingMethod')}
            initialValue={true}
          >
            <Select style={selectThemeStyle}>
              <Option value={true}>{t('user:password.autoGenerate')}</Option>
              <Option value={false}>{t('user:password.manualSet')}</Option>
            </Select>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.generateNew !== currentValues.generateNew
            }
          >
            {({ getFieldValue }) => {
              if (!getFieldValue('generateNew')) {
                return (
                  <>
                    <Form.Item
                      name="newPassword"
                      label={t('user:password.new')}
                      rules={[
                        {
                          required: true,
                          message: t('user:validation.passwordRequired'),
                        },
                        { min: 8, message: t('user:password.minLength') },
                        {
                          pattern:
                            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])/,
                          message: t('user:validation.passwordComplexity'),
                        },
                      ]}
                    >
                      <Input.Password
                        placeholder={t('user:password.newPlaceholder')}
                        style={inputThemeStyle}
                      />
                    </Form.Item>
                    <Form.Item
                      name="confirmPassword"
                      label={t('user:password.confirm')}
                      dependencies={['newPassword']}
                      rules={[
                        {
                          required: true,
                          message: t('user:validation.confirmPasswordRequired'),
                        },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (
                              !value ||
                              getFieldValue('newPassword') === value
                            ) {
                              return Promise.resolve();
                            }
                            return Promise.reject(
                              new Error(t('user:validation.passwordMismatch'))
                            );
                          },
                        }),
                      ]}
                    >
                      <Input.Password
                        placeholder={t('user:password.confirmPlaceholder')}
                        style={inputThemeStyle}
                      />
                    </Form.Item>
                  </>
                );
              }
              return null;
            }}
          </Form.Item>

          <Form.Item
            name="forceChange"
            valuePropName="checked"
            initialValue={false}
          >
            <Tooltip title={t('user:password.forceChangeTooltip')}>
              <span>{t('user:password.forceChange')}</span>
            </Tooltip>
          </Form.Item>
        </Form>
      </Modal>
    </StandardPageLayout>
  );
};

export default UserManagement;
